/**
 * ymhEntrytestV2 — Alpine.js data component for the Einstiegstest V2 quiz.
 *
 * Quiz steps:
 *   1. Level         — native range slider
 *   2. Fokus         — up to 2 photo cards (multi-select)
 *   3. Themen        — up to 12 topic cards (based on chosen Foki)
 *   4. Dauer         — window slider (same as video filter bar)
 *   5. Intensität    — 3 photo cards (sanft / mittel / schweißtreibend)
 *
 * Features:
 *  - Slide-in transition (forward = from right, backward = from left)
 *  - Pulse animation feedback on card selection
 *  - Click-to-position on all sliders (including axis labels)
 *  - Teacher data loaded from data attribute (legacy — unused after rework)
 *
 * Agent-authored: EntrytestV2 feature (2026)
 */

// ─── Types ──────────────────────────────────────────────────────────────────

export interface EntrytestV2Answers {
  experience:   string | null;
  /** Up to 2 motivation keys (e.g. ['physical','relax']). */
  motivations:  string[];
  /** Selected topic slugs (e.g. ['rueck','meditation']). */
  topics:       string[];
  /** Format: numeric string with target minutes ("30") or open bucket ("120+"). */
  duration:     string | null;
  intensity:    string | null;
}

type NavDirection = 'forward' | 'backward' | 'none';

/** Single selectable topic in step 3. */
export interface TopicOption {
  /** Stable slug used as form value. */
  slug:  string;
  /** Display label. */
  label: string;
  /** The motivation this topic belongs to (used for the 6/6 quota split). */
  focus: string;
}

// ─── Constants ──────────────────────────────────────────────────────────────

const EXPERIENCE_VALUES = ['beginner', 'foundations', 'regular', 'expert'] as const;

const BASE_STEPS = 5;
const STEP_LABELS = ['Level', 'Fokus', 'Themen', 'Dauer', 'Intensität'] as const;

const MAX_MOTIVATIONS = 2;
const TOPIC_MAX = 8;            // hard cap on user selection

/**
 * Step 3 column layout: each focus block spans 2 columns × DEFAULT_ROWS rows
 * (6 tiles visible by default). When the focus has more topics, a "Mehr zeigen"
 * button below the block reveals the rest.
 */
const STEP3_DEFAULT_ROWS = 3;
const STEP3_TILES_PER_ROW_IN_BLOCK = 2;  // each focus block has 2 columns
const STEP3_DEFAULT_VISIBLE_PER_FOCUS = STEP3_DEFAULT_ROWS * STEP3_TILES_PER_ROW_IN_BLOCK;  // 6

// 3 visible by default — matches the 3-row default of focus blocks (1 tile per row,
// 1 column). "Mehr zeigen" appears after the 3rd tile, consistent with all other blocks.
const STEP3_DEFAULT_VISIBLE_FOR_SPECIALS = 3;

/**
 * Tiles shown in the Specials block on Step 3.
 *
 * Order matters: the first STEP3_DEFAULT_VISIBLE_FOR_SPECIALS entries are
 * visible by default, the rest appear after the "Mehr zeigen" toggle.
 *
 * Pilates intentionally reuses the same slug as the Fitness-block Pilates
 * tile — selecting either tile keeps a single 'pilates' entry in
 * answers.topics, and both tiles render as active.
 */
const SPECIAL_TOPICS: TopicOption[] = [
  { slug: 'morgen-yoga', label: 'Morgen-Yoga',        focus: 'special' },
  { slug: 'schwangere',  label: 'Schwangere & Mamis', focus: 'special' },
  { slug: 'pilates',     label: 'Pilates',            focus: 'special' },
  { slug: 'qigong',      label: 'QiGong',             focus: 'special' },
  // ── below the "Mehr zeigen" fold ──
  { slug: 'yin-yoga',    label: 'Yin Yoga',           focus: 'special' },
];

/**
 * Headlines for the focus column blocks on Step 3. Mirrors the Step 2 focus
 * card labels.
 *
 * The explicit `<br>` after the first word forces a two-line layout on every
 * focus headline so all focus blocks share the same visual rhythm regardless
 * of label width. Rendered via `x-html` in Step3Topics.html (the source is
 * this static map, never user input → XSS-safe).
 */
const FOCUS_HEADLINES: Record<string, string> = {
  physical:  'Körper &<br>Fitness',
  emotional: 'Herz &<br>Seele',
  relax:     'Meditation &<br>Entspannung',
  therapy:   'Therapie &<br>Heilung',
};

/**
 * Canonical left-to-right order of the focus cards on Step 2.
 * Step 3 blocks render in this same order regardless of click sequence
 * so users see a stable layout between the two steps.
 * Keep in sync with the card order in Step2Motivation.html.
 */
const FOCUS_DISPLAY_ORDER = ['physical', 'emotional', 'relax', 'therapy'] as const;

/**
 * Topics per motivation focus (NO specials — those live in SPECIAL_TOPICS).
 * Order matters: top-listed topics appear first in the visible 6.
 */
const TOPICS_BY_FOCUS: Record<string, TopicOption[]> = {
  physical: [
    { slug: 'ganzkoerper',    label: 'Ganzkörper Workout',  focus: 'physical' },
    { slug: 'mitte',          label: 'Mitte stärken',       focus: 'physical' },
    { slug: 'beine-po',       label: 'Beine & Po',          focus: 'physical' },
    { slug: 'faszien',        label: 'Faszien',             focus: 'physical' },
    { slug: 'gleichgewicht',  label: 'Gleichgewicht',       focus: 'physical' },
    { slug: 'pilates',        label: 'Pilates',             focus: 'physical' },
  ],
  emotional: [
    { slug: 'herzoeffner',    label: 'Herzöffner',          focus: 'emotional' },
    { slug: 'weiblichkeit',   label: 'Weiblichkeit',        focus: 'emotional' },
    { slug: 'chakren',        label: 'Chakren',             focus: 'emotional' },
    { slug: 'mantren',        label: 'Mantren',             focus: 'emotional' },
    { slug: 'astrologie',     label: 'Astrologie',          focus: 'emotional' },
  ],
  relax: [
    { slug: 'entspannung',    label: 'Entspannung',         focus: 'relax' },
    { slug: 'meditation',     label: 'Meditation',          focus: 'relax' },
    { slug: 'stress',         label: 'Stress',              focus: 'relax' },
    { slug: 'atem',           label: 'Atem',                focus: 'relax' },
    { slug: 'nervensystem',   label: 'Nervensystem',        focus: 'relax' },
    { slug: 'yoga-nidra',     label: 'Yoga Nidra',          focus: 'relax' },
    { slug: 'schlaf',         label: 'Schlaf',              focus: 'relax' },
  ],
  therapy: [
    { slug: 'nacken',         label: 'Hals & Nacken',       focus: 'therapy' },
    { slug: 'rueck',          label: 'Rücken',              focus: 'therapy' },
    // stress + nervensystem are cross-listed here (canonical home focus: relax).
    // The 'focus' field tracks the slug's home focus, not the block it appears in.
    { slug: 'stress',         label: 'Stress',              focus: 'relax' },
    { slug: 'nervensystem',   label: 'Nervensystem',        focus: 'relax' },
    { slug: 'huefte',         label: 'Hüfte',               focus: 'therapy' },
    { slug: 'knie',           label: 'Knie',                focus: 'therapy' },
    { slug: 'menstruation',   label: 'Menstruation',        focus: 'therapy' },
    { slug: 'wechseljahre',   label: 'Wechseljahre',        focus: 'therapy' },
    { slug: 'beckenboden',    label: 'Beckenboden',         focus: 'therapy' },
    { slug: 'stuhl',          label: 'Yoga am Stuhl',       focus: 'therapy' },
    { slug: 'angst',          label: 'Angst & Unruhe',      focus: 'therapy' },
    { slug: 'immunsystem',    label: 'Immunsystem',         focus: 'therapy' },
    { slug: 'verdauung',      label: 'Verdauung',           focus: 'therapy' },
  ],
};

/**
 * One block rendered on Step 3. A block is either a focus block (2 columns
 * wide, STEP3_DEFAULT_VISIBLE_PER_FOCUS tiles visible by default) or the
 * Specials block (1 column wide, STEP3_DEFAULT_VISIBLE_FOR_SPECIALS tiles
 * visible by default). Both expand via the "Mehr zeigen" link below the
 * block when `hasMore` is true.
 */
export interface TopicBlock {
  /** Stable key — focus key ('physical' etc.) or 'special'. */
  key:        string;
  /** Headline shown above the block. */
  headline:   string;
  /** All tiles in this block (without slicing for visibility). */
  topics:     TopicOption[];
  /** True when more topics exist than DEFAULT_VISIBLE — toggles "Mehr zeigen". */
  hasMore:    boolean;
  /** Whether the user clicked "Mehr zeigen" to expand this block. */
  isExpanded: boolean;
  /** Tiles to actually render right now (sliced if collapsed). */
  visible:    TopicOption[];
  /** Grid layout: 2 for foci, 1 for specials. */
  columns:    number;
}

const PULSE_MS = 300;

// ─── Duration slider (Step 4) — same constants as video filter bar ──────────

const DUR_WINDOW = 10;
const DUR_STEP = 5;
const DUR_UI_MAX = 120;
const DUR_BREAK_MIN = 60;
const DUR_BREAK_PCT = 0.6;
const DUR_MAX_PCT = 0.95;
const DUR_DB_MAX = 120;

function durMinToPct(min: number): number {
  if (min <= 0) return 0;
  if (min <= DUR_BREAK_MIN) {
    return (min / DUR_BREAK_MIN) * DUR_BREAK_PCT;
  }
  if (min <= DUR_UI_MAX) {
    return DUR_BREAK_PCT
      + ((min - DUR_BREAK_MIN) / (DUR_UI_MAX - DUR_BREAK_MIN))
      * (DUR_MAX_PCT - DUR_BREAK_PCT);
  }
  return 1;
}

function durPctToMin(pct: number): number {
  if (pct <= 0) return 0;
  if (pct >= 1) return DUR_UI_MAX;
  if (pct <= DUR_BREAK_PCT) {
    return (pct / DUR_BREAK_PCT) * DUR_BREAK_MIN;
  }
  if (pct <= DUR_MAX_PCT) {
    return DUR_BREAK_MIN
      + ((pct - DUR_BREAK_PCT) / (DUR_MAX_PCT - DUR_BREAK_PCT))
      * (DUR_UI_MAX - DUR_BREAK_MIN);
  }
  return DUR_UI_MAX;
}

// ─── Component ──────────────────────────────────────────────────────────────

export function ymhEntrytestV2() {
  return {
    // ──────────────────────────────────────────────────────────────────
    // Reactive state
    // ──────────────────────────────────────────────────────────────────

    currentStep:  1 as number,
    navDirection: 'forward' as NavDirection,
    pulsingValue: null as string | null,
    /** LP standalone submit phase: false | 'loading' | 'success' | 'done' */
    isSubmitting: false as false | string,

    /** Native range slider position for Step 1 (Level). 0 = "Neu im Yoga". */
    experienceIndex: 0 as number,

    /** Duration window edges in minutes — initialised so the window is visible. */
    durMin: 0 as number | null,
    durMax: DUR_WINDOW as number | null,
    durDbMax: DUR_DB_MAX,

    // Internal drag state
    _durDragging: false,
    _durStartX:   0,
    _durStartMin: 0,

    answers: {
      experience:  'beginner',
      motivations: [] as string[],
      topics:      [] as string[],
      duration:    null,
      intensity:   null,
    } as EntrytestV2Answers,

    /**
     * Step 3 — set of focus keys for which the user clicked "Mehr zeigen".
     * Stored as a plain object for Alpine reactivity (Sets don't track changes).
     */
    expandedFoci: {} as Record<string, boolean>,

    // ──────────────────────────────────────────────────────────────────
    // Lifecycle
    // ──────────────────────────────────────────────────────────────────

    init() {
      this._writeDurationAnswer();
      this._initSwipe();
    },

    // ──────────────────────────────────────────────────────────────────
    // Computed
    // ──────────────────────────────────────────────────────────────────

    get effectiveTotalSteps(): number {
      return BASE_STEPS;
    },

    get stepLabels(): string[] {
      return [...STEP_LABELS];
    },

    /**
     * Step 3 column blocks: one per chosen focus (up to 2), followed by the
     * Specials block. Focus blocks span 2 grid columns and show up to
     * STEP3_DEFAULT_VISIBLE_PER_FOCUS tiles by default. The Specials block
     * spans 1 column and shows STEP3_DEFAULT_VISIBLE_FOR_SPECIALS tiles by
     * default. Both expand via the "Mehr zeigen" link when `hasMore` is true.
     *
     * Focus blocks are rendered in the canonical Step 2 order
     * (FOCUS_DISPLAY_ORDER), not in the order the user clicked them, so the
     * visual layout stays consistent between Step 2 and Step 3.
     */
    get topicBlocks(): TopicBlock[] {
      const blocks: TopicBlock[] = [];

      const orderedFoci = FOCUS_DISPLAY_ORDER.filter(
        f => this.answers.motivations.includes(f),
      );

      // Round up to an even count so the grid never ends with a lone tile
      // in a half-empty row. Only rounds up when more topics are available.
      const evenSlice = (all: TopicOption[], defaultCount: number): TopicOption[] => {
        const count = (defaultCount % 2 !== 0 && all.length > defaultCount)
          ? defaultCount + 1
          : defaultCount;
        return all.slice(0, count);
      };

      for (const focus of orderedFoci) {
        const all      = TOPICS_BY_FOCUS[focus] ?? [];
        const expanded = !!this.expandedFoci[focus];
        const visible  = expanded ? all : evenSlice(all, STEP3_DEFAULT_VISIBLE_PER_FOCUS);
        const hasMore  = all.length > visible.length;

        blocks.push({
          key:        focus,
          headline:   FOCUS_HEADLINES[focus] ?? focus,
          topics:     all,
          hasMore,
          isExpanded: expanded,
          visible,
          columns:    2,
        });
      }

      // Specials block is always shown once a focus is chosen so the layout
      // doesn't jump around when the user toggles foci on/off.
      if (this.answers.motivations.length > 0) {
        const specialsExpanded = !!this.expandedFoci['special'];
        // Specials block is 1-column — no even-rounding needed, use plain slice.
        const specialsVisible  = specialsExpanded
          ? SPECIAL_TOPICS
          : SPECIAL_TOPICS.slice(0, STEP3_DEFAULT_VISIBLE_FOR_SPECIALS);
        const specialsHasMore  = SPECIAL_TOPICS.length > specialsVisible.length;
        blocks.push({
          key:        'special',
          headline:   'Bonus',
          topics:     SPECIAL_TOPICS,
          hasMore:    specialsHasMore,
          isExpanded: specialsExpanded,
          visible:    specialsVisible,
          columns:    1,
        });
      }

      return blocks;
    },

    /** Flat list of every visible tile across all blocks. Kept for
     *  backwards-compatibility and for empty-state checks. */
    get currentTopics(): TopicOption[] {
      return this.topicBlocks.flatMap(b => b.visible);
    },

    /** Whether a focus block currently shows all its tiles. */
    isFocusExpanded(focus: string): boolean {
      return !!this.expandedFoci[focus];
    },

    /** Toggle "Mehr zeigen" for a focus block. */
    toggleFocusExpand(focus: string): void {
      this.expandedFoci[focus] = !this.expandedFoci[focus];
    },

    /** Whether the user has provided a valid answer for the current step. */
    get canProceed(): boolean {
      switch (this.currentStep) {
        case 1: return this.answers.experience  !== null;
        case 2: return this.answers.motivations.length > 0;
        case 3: return this.answers.topics.length >= 1 && this.answers.topics.length <= TOPIC_MAX;
        case 4: return this.answers.duration    !== null;
        case 5: return this.answers.intensity   !== null;
        default: return false;
      }
    },

    // ──────────────────────────────────────────────────────────────────
    // Step 1 — Level slider
    // ──────────────────────────────────────────────────────────────────

    onExperienceSlider(index: number): void {
      this.experienceIndex    = index;
      this.answers.experience = EXPERIENCE_VALUES[index] ?? null;
      this._updateRangeFill(index);
    },

    // ──────────────────────────────────────────────────────────────────
    // Step 2 — Fokus multi-select (max 2)
    // ──────────────────────────────────────────────────────────────────

    /** Toggle a motivation. Selecting more than MAX_MOTIVATIONS is blocked. */
    toggleMotivation(value: string): void {
      const list = this.answers.motivations;
      const idx  = list.indexOf(value);

      if (idx >= 0) {
        // Already selected — deselect it (and clear topics outside the new selection)
        list.splice(idx, 1);
      } else if (list.length < MAX_MOTIVATIONS) {
        list.push(value);
      } else {
        // Limit reached — pulse feedback but no add
        this._pulse(value);
        return;
      }

      this._pulse(value);
      this._dropTopicsOutsideCurrentFoci();
    },

    isMotivationSelected(value: string): boolean {
      return this.answers.motivations.includes(value);
    },

    // ──────────────────────────────────────────────────────────────────
    // Step 3 — Themen multi-select (max TOPIC_MAX = 8)
    // ──────────────────────────────────────────────────────────────────

    toggleTopic(slug: string): void {
      const list = this.answers.topics;
      const idx  = list.indexOf(slug);
      if (idx >= 0) {
        list.splice(idx, 1);
      } else {
        if (list.length >= TOPIC_MAX) {
          // Ignore further selections, keep visual pulse to acknowledge click
          this._pulse(slug);
          return;
        }
        list.push(slug);
      }
      this._pulse(slug);
    },

    isTopicSelected(slug: string): boolean {
      return this.answers.topics.includes(slug);
    },

    // ──────────────────────────────────────────────────────────────────
    // Step 4 — Duration window slider
    // ──────────────────────────────────────────────────────────────────

    durAxisPct(minutes: number): number {
      return durMinToPct(minutes);
    },

    durWindowPos(): number {
      const lo = this.durMin ?? 0;
      if (lo >= DUR_UI_MAX) return DUR_MAX_PCT;
      return durMinToPct(lo);
    },

    durWindowWidth(): number {
      const lo = this.durMin ?? 0;
      if (lo >= DUR_UI_MAX) return 1 - DUR_MAX_PCT;
      const hi = this.durMax ?? this.durDbMax;
      const leftPct  = durMinToPct(lo);
      const rightPct = hi >= DUR_UI_MAX ? 1 : durMinToPct(hi);
      return Math.max(0, rightPct - leftPct);
    },

    durDragStart(e: MouseEvent | TouchEvent): void {
      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      this._durJumpWindowTo(clientX);
      this._durDragging = true;
      this._durStartX   = clientX;
      this._durStartMin = this.durMin ?? 0;
      e.preventDefault();
    },

    durDragMove(e: MouseEvent | TouchEvent): void {
      if (!this._durDragging) return;
      const trackEl = document.querySelector<HTMLElement>('.ymh-et2-dur-track');
      if (!trackEl) return;
      const rect = trackEl.getBoundingClientRect();
      if (rect.width <= 0) {
        this._durDragging = false;
        return;
      }

      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;

      const cursorPct       = (clientX - rect.left) / rect.width;
      const startLeftPct    = durMinToPct(this._durStartMin);
      const startRightPct   = durMinToPct(this._durStartMin + DUR_WINDOW);
      const startCenterPct  = (startLeftPct + startRightPct) / 2;
      const startCursorPct  = (this._durStartX - rect.left) / rect.width;
      const grabOffsetPct   = startCursorPct - startCenterPct;
      const newCenterPct    = cursorPct - grabOffsetPct;

      let newMin = durPctToMin(newCenterPct) - DUR_WINDOW / 2;
      newMin     = Math.round(newMin / DUR_STEP) * DUR_STEP;
      newMin     = Math.max(0, Math.min(newMin, DUR_UI_MAX - DUR_WINDOW));

      if (newMin + DUR_WINDOW >= DUR_UI_MAX) {
        this.durMin = DUR_UI_MAX;
        this.durMax = this.durDbMax;
      } else {
        this.durMin = newMin;
        this.durMax = newMin + DUR_WINDOW;
      }

      this._writeDurationAnswer();
    },

    durDragEnd(): void {
      this._durDragging = false;
    },

    durJumpToMinutes(minutes: number): void {
      let newMin = Math.round((minutes - DUR_WINDOW / 2) / DUR_STEP) * DUR_STEP;
      newMin     = Math.max(0, Math.min(newMin, DUR_UI_MAX - DUR_WINDOW));

      if (minutes >= DUR_UI_MAX || newMin + DUR_WINDOW >= DUR_UI_MAX) {
        this.durMin = DUR_UI_MAX;
        this.durMax = this.durDbMax;
      } else {
        this.durMin = newMin;
        this.durMax = newMin + DUR_WINDOW;
      }

      this._writeDurationAnswer();
    },

    // ──────────────────────────────────────────────────────────────────
    // Selection helpers, pulse feedback
    // ──────────────────────────────────────────────────────────────────

    /** Set a scalar answer with pulse animation feedback. */
    select<K extends keyof EntrytestV2Answers>(field: K, value: EntrytestV2Answers[K]): void {
      this.answers[field] = value;
      this._pulse(String(value));
    },

    isPulsing(value: string): boolean {
      return this.pulsingValue === value;
    },

    // ──────────────────────────────────────────────────────────────────
    // Navigation
    // ──────────────────────────────────────────────────────────────────

    next(): void {
      if (!this.canProceed) return;
      if (this.currentStep >= this.effectiveTotalSteps) {
        this.submitForm();
        return;
      }
      this.navDirection = 'forward';
      this.currentStep++;
      this._scrollToAnchor();
    },

    back(): void {
      if (this.currentStep <= 1) return;
      this.navDirection = 'backward';
      this.currentStep--;
      this._scrollToAnchor();
    },

    submitForm(): void {
      const form = document.getElementById('ymh-entrytest-v2-form') as HTMLFormElement | null;
      if (!form) return;

      const anchor = document.getElementById('ymh-entrytest-v2-anchor');
      const isEmbed     = anchor?.dataset?.embed === '1';
      const isStandalone = anchor?.dataset?.embed === '0';

      if (isStandalone) {
        // LP standalone mode: intercept submit, run AJAX flow via
        // window.ymhEntrytestV2LpSubmit() set up by Index.html.
        // The handler shows loading/success phases and renders results inline.
        (this as any).isSubmitting = true;
        const handler = (window as any).ymhEntrytestV2LpSubmit;
        if (typeof handler === 'function') {
          handler(form);
        } else {
          // Fallback if handler not ready: normal page submit
          form.submit();
        }
        return;
      }

      // requestSubmit() fires the 'submit' event so the dashboard embed
      // interceptor can preventDefault() and post via fetch.
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
      } else {
        const evt = new Event('submit', { bubbles: true, cancelable: true });
        const cancelled = !form.dispatchEvent(evt);
        if (!cancelled) form.submit();
      }
    },

    // ──────────────────────────────────────────────────────────────────
    // Private helpers
    // ──────────────────────────────────────────────────────────────────

    _pulse(value: string): void {
      this.pulsingValue = value;
      window.setTimeout(() => { this.pulsingValue = null; }, PULSE_MS);
    },

    _updateRangeFill(index: number): void {
      const pct = index < 0 ? 0 : (index / 3) * 100;
      const el  = document.querySelector<HTMLElement>('.ymh-et2-range');
      el?.style.setProperty('--range-pct', pct + '%');
    },

    _scrollToAnchor(): void {
      const anchor = document.getElementById('ymh-entrytest-v2-anchor');
      if (!anchor) return;

      // Dashboard-embed mode: the quiz lives inside Mein Bereich and
      // there's a sticky page header (+ overhanging logo) covering the
      // top portion of the viewport. The dashboard exposes its own
      // obstruction-aware smooth-scroll on window so step transitions
      // pin the test below the header instead of letting the chrome
      // slide over the quiz. Standalone /yoga-test-neu has no such
      // obstruction so the regular scrollIntoView is fine there.
      // Agent-authored: 2026-06.
      const embedRoot = anchor.closest('.ymh-entrytest-v2--embed');
      const embedScroll = (window as unknown as {
        ymhEt2EmbedScrollHandler?: () => void;
      }).ymhEt2EmbedScrollHandler;
      if (embedRoot && typeof embedScroll === 'function') {
        embedScroll();
        return;
      }

      anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },

    /**
     * Register touch-swipe navigation on the quiz content area (mobile only).
     *
     * The inner content wrapper follows the finger in real-time so the user
     * sees the page sliding while they drag. On release:
     *   - If the horizontal delta exceeds SWIPE_THRESHOLD and the gesture is
     *     valid (canProceed for forward, always for backward): the step
     *     changes and the wrapper snaps to its resting position via a short
     *     CSS transition.
     *   - Otherwise the wrapper springs back to x=0.
     *
     * Only activates on coarse-pointer devices (touch phones/tablets).
     * Ignores touches starting on interactive elements so sliders, tiles and
     * focus-cards still receive their own touch events.
     */
    _initSwipe(): void {
      if (!window.matchMedia('(pointer: coarse)').matches) return;

      const quiz    = document.querySelector<HTMLElement>('.ymh-entrytest-v2__quiz');
      const content = document.querySelector<HTMLElement>('.ymh-et2-content');
      if (!quiz || !content) return;

      const SWIPE_THRESHOLD = 60;
      const DRAG_RESISTANCE = 0.4;   // finger moves 100px → content moves 40px
      const SNAP_DURATION   = '0.28s';
      const SPRING_DURATION = '0.22s';

      const INTERACTIVE = [
        'input', 'button', 'select', 'textarea',
        '[role="slider"]',
        '.ymh-et2-topic-tile',
        '.ymh-et2-focus-card',
        '.ymh-et2-topic-block-more',
        '.ymh-slider-axis',
      ].join(', ');

      let startX    = 0;
      let startY    = 0;
      let active    = false;
      let cancelled = false;   // set true when vertical motion dominates

      const setTranslate = (x: number, duration: string) => {
        content.style.transition = x === 0 ? `transform ${duration} ease` : 'none';
        content.style.transform  = x === 0 ? ''                            : `translateX(${x}px)`;
      };

      const release = (committed: boolean, forward: boolean) => {
        if (committed) {
          // Continue sliding off-screen in the same direction the finger went.
          // Once off-screen, change the step WITHOUT Alpine's built-in slide
          // animation (navDirection = 'none'), then instantly reset the
          // transform so the new step appears at rest with no visible jump.
          const offscreen = forward ? -window.innerWidth : window.innerWidth;
          content.style.transition = `transform ${SNAP_DURATION} ease`;
          content.style.transform  = `translateX(${offscreen}px)`;

          window.setTimeout(() => {
            // Disable Alpine slide animation for this navigation.
            this.navDirection = 'none';
            if (forward) {
              if (this.currentStep >= this.effectiveTotalSteps) {
                this.submitForm();
              } else {
                this.currentStep++;
                this._scrollToAnchor();
              }
            } else {
              if (this.currentStep > 1) {
                this.currentStep--;
                this._scrollToAnchor();
              }
            }
            // Snap back to rest instantly — new step is already rendered there.
            content.style.transition = 'none';
            content.style.transform  = '';
            // Restore navDirection after Alpine re-renders.
            window.requestAnimationFrame(() => {
              this.navDirection = forward ? 'forward' : 'backward';
            });
          }, parseFloat(SNAP_DURATION) * 1000);
        } else {
          // Threshold not reached — spring back with a slight elastic overshoot.
          content.style.transition = `transform ${SPRING_DURATION} cubic-bezier(0.34,1.56,0.64,1)`;
          content.style.transform  = '';
          window.setTimeout(() => {
            content.style.transition = '';
          }, parseFloat(SPRING_DURATION) * 1000);
        }
      };

      quiz.addEventListener('touchstart', (e: TouchEvent) => {
        if ((e.target as Element)?.closest(INTERACTIVE)) {
          active = false;
          return;
        }
        const t = e.touches[0];
        startX    = t.clientX;
        startY    = t.clientY;
        active    = true;
        cancelled = false;
        content.style.transition = 'none';
      }, { passive: true });

      quiz.addEventListener('touchmove', (e: TouchEvent) => {
        if (!active) return;
        const t  = e.touches[0];
        const dx = t.clientX - startX;
        const dy = t.clientY - startY;

        // First significant movement: decide scroll vs swipe.
        if (!cancelled && Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 8) {
          cancelled = true;
          setTranslate(0, SPRING_DURATION);
          return;
        }
        if (cancelled) return;

        // Clamp drag: block forward if canProceed is false, allow full back.
        const forwardBlocked = dx < 0 && !this.canProceed;
        const clampedDx      = forwardBlocked ? Math.min(dx, 0) * 0.15 : dx * DRAG_RESISTANCE;

        content.style.transition = 'none';
        content.style.transform  = `translateX(${clampedDx}px)`;
      }, { passive: true });

      quiz.addEventListener('touchend', (e: TouchEvent) => {
        if (!active || cancelled) {
          active = false;
          return;
        }
        active = false;
        const t  = e.changedTouches[0];
        const dx = t.clientX - startX;
        const dy = t.clientY - startY;

        if (Math.abs(dy) > Math.abs(dx)) { release(false, false); return; }
        if (Math.abs(dx) < SWIPE_THRESHOLD) { release(false, false); return; }

        const forward = dx < 0;
        if (forward && !this.canProceed) { release(false, false); return; }
        release(true, forward);
      }, { passive: true });
    },

    _writeDurationAnswer(): void {
      if (this.durMin === null) { this.answers.duration = null; return; }
      if (this.durMin >= DUR_UI_MAX) { this.answers.duration = '120+'; return; }
      const lo     = this.durMin;
      const hi     = this.durMax ?? lo + DUR_WINDOW;
      const center = Math.round((lo + hi) / 2);
      this.answers.duration = String(center);
    },

    /** Drop any selected topics whose focus is no longer in the motivations list. */
    _durJumpWindowTo(clientX: number): void {
      const trackEl = document.querySelector<HTMLElement>('.ymh-et2-dur-track');
      if (!trackEl) return;
      const rect = trackEl.getBoundingClientRect();
      if (rect.width <= 0) return;

      const cursorPct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      let newMin = durPctToMin(cursorPct) - DUR_WINDOW / 2;
      newMin     = Math.round(newMin / DUR_STEP) * DUR_STEP;
      newMin     = Math.max(0, Math.min(newMin, DUR_UI_MAX - DUR_WINDOW));

      if (newMin + DUR_WINDOW >= DUR_UI_MAX) {
        this.durMin = DUR_UI_MAX;
        this.durMax = this.durDbMax;
      } else {
        this.durMin = newMin;
        this.durMax = newMin + DUR_WINDOW;
      }
      this._writeDurationAnswer();
    },

    _dropTopicsOutsideCurrentFoci(): void {
      // Specials slugs are always allowed; they survive any focus change.
      const allowedSlugs = new Set<string>(SPECIAL_TOPICS.map(t => t.slug));
      for (const focus of this.answers.motivations) {
        for (const t of TOPICS_BY_FOCUS[focus] ?? []) {
          allowedSlugs.add(t.slug);
        }
      }
      this.answers.topics = this.answers.topics.filter(s => allowedSlugs.has(s));
    },
  };
}
